// src/components/GridView/ManageColumnsModal.tsx
import {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import type { GridColumn } from './types';
import { getColumnApiKey } from './hooks/useHiddenColumns';

// ==================== Props ====================

interface ManageColumnsModalProps {
  open: boolean;
  onClose: () => void;
  /** All dynamic columns (not title / created-at), including hidden ones */
  allDynamicColumns: GridColumn[];
  hiddenKeys: Set<string>;
  onApply: (newHiddenKeys: string[]) => void;
}

// ==================== Indeterminate Checkbox ====================

interface LayerCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  onChange: (e: React.MouseEvent) => void;
}

function LayerCheckbox({ checked, indeterminate, onChange }: LayerCheckboxProps)
{
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() =>
  {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      readOnly
      onClick={onChange}
      style={{ accentColor: '#5dade2', cursor: 'pointer', width: 14, height: 14, flexShrink: 0 }}
    />
  );
}

// ==================== Component ====================

export function ManageColumnsModal({
  open,
  onClose,
  allDynamicColumns,
  hiddenKeys,
  onApply,
}: ManageColumnsModalProps)
{
  const [localHidden, setLocalHidden] = useState<Set<string>>(() => new Set(hiddenKeys));
  // All layers expanded by default; collapsed ones stored here
  const [collapsedLayers, setCollapsedLayers] = useState<Set<number>>(new Set());

  // Refs for drag-without-rerender
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  } | null>(null);

  // Sync local state when modal opens
  useEffect(() =>
  {
    if (open)
    {
      setLocalHidden(new Set(hiddenKeys));
      // Start with all layers collapsed (user expands as needed)
      const allLayerIds = new Set(
        allDynamicColumns.map(col => col.layerId).filter((id): id is number => id != null),
      );
      setCollapsedLayers(allLayerIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape key + global drag listeners (mounted while modal is open)
  useEffect(() =>
  {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape') onClose();
    };

    const handleMouseMove = (e: MouseEvent) =>
    {
      if (!dragRef.current || !panelRef.current) return;
      const { startX, startY, startLeft, startTop } = dragRef.current;
      const panel = panelRef.current;
      const newLeft = Math.max(
        0,
        Math.min(startLeft + (e.clientX - startX), window.innerWidth - panel.offsetWidth),
      );
      const newTop = Math.max(
        0,
        Math.min(startTop + (e.clientY - startY), window.innerHeight - panel.offsetHeight),
      );
      panel.style.left = `${newLeft}px`;
      panel.style.top = `${newTop}px`;
    };

    const handleMouseUp = () =>
    {
      dragRef.current = null;
      document.body.style.cursor = '';
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () =>
    {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [open, onClose]);

  const handleHeaderMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) =>
  {
    if (e.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    // Convert from transform-based centering to absolute pixel coords (one-time)
    panel.style.transform = 'none';
    panel.style.left = `${rect.left}px`;
    panel.style.top = `${rect.top}px`;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };
    document.body.style.cursor = 'grabbing';
    e.preventDefault();
  }, []);

  // Group columns by layerId
  const groups = useMemo(() =>
  {
    const map = new Map<
      number,
      { layerName: string; layerColor?: string; columns: GridColumn[] }
    >();
    for (const col of allDynamicColumns)
    {
      if (col.layerId == null) continue;
      if (!map.has(col.layerId))
      {
        map.set(col.layerId, {
          layerName: col.layerName ?? `Layer ${col.layerId}`,
          layerColor: col.layerColor,
          columns: [],
        });
      }
      map.get(col.layerId)!.columns.push(col);
    }
    return Array.from(map.entries()).map(([layerId, data]) => ({ layerId, ...data }));
  }, [allDynamicColumns]);

  const toggleCollapse = useCallback((layerId: number) =>
  {
    setCollapsedLayers(prev =>
    {
      const next = new Set(prev);
      if (next.has(layerId)) next.delete(layerId);
      else next.add(layerId);
      return next;
    });
  }, []);

  const toggleColumn = useCallback((key: string) =>
  {
    setLocalHidden(prev =>
    {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleLayer = useCallback(
    (e: React.MouseEvent, cols: GridColumn[]) =>
    {
      e.stopPropagation(); // don't toggle accordion
      const keys = cols.map(getColumnApiKey);
      const allVisible = keys.every(k => !localHidden.has(k));
      setLocalHidden(prev =>
      {
        const next = new Set(prev);
        if (allVisible) keys.forEach(k => next.add(k));
        else keys.forEach(k => next.delete(k));
        return next;
      });
    },
    [localHidden],
  );

  const handleApply = useCallback(() =>
  {
    onApply(Array.from(localHidden));
    onClose();
  }, [localHidden, onApply, onClose]);

  if (!open) return null;

  const isDark = document.documentElement.classList.contains('dark');

  const modal = (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 3000,
          backgroundColor: 'rgba(0,0,0,0.35)',
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          zIndex: 3001,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520,
          maxWidth: 'calc(100vw - 32px)',
          backgroundColor: isDark ? '#333535' : '#fff',
          border: `1px solid ${isDark ? '#4a4c4c' : '#d1d5db'}`,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        {/* Header — drag handle */}
        <div
          onMouseDown={handleHeaderMouseDown}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: `1px solid ${isDark ? '#4a4c4c' : '#e5e7eb'}`,
            flexShrink: 0,
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          <span style={{ color: '#4887be', fontWeight: 600, fontSize: 14 }}>
            Customize Grid View
          </span>
          <button
            onClick={onClose}
            onMouseDown={e => e.stopPropagation()}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isDark ? '#6e6860' : '#aaa',
              fontSize: 20,
              lineHeight: 1,
              padding: 0,
            }}
            onMouseEnter={e =>
            {
              e.currentTarget.style.color = isDark ? '#a9a093' : '#777';
            }}
            onMouseLeave={e =>
            {
              e.currentTarget.style.color = isDark ? '#6e6860' : '#aaa';
            }}
          >
            &times;
          </button>
        </div>

        {/* Column groups */}
        <div style={{ maxHeight: 420, overflowY: 'auto', padding: '4px 0' }}>
          {groups.length === 0 && (
            <div style={{ padding: '16px', color: isDark ? '#6e6860' : '#9ca3af', fontSize: 13 }}>
              No columns available.
            </div>
          )}
          {groups.map(group =>
          {
            const keys = group.columns.map(getColumnApiKey);
            const visibleCount = keys.filter(k => !localHidden.has(k)).length;
            const allVisible = visibleCount === keys.length;
            const someVisible = visibleCount > 0 && visibleCount < keys.length;
            const isCollapsed = collapsedLayers.has(group.layerId);

            return (
              <div
                key={group.layerId}
                style={{ borderBottom: `1px solid ${isDark ? '#4a4c4c' : '#f0f0f0'}` }}
              >
                {/* Layer header — click to toggle accordion */}
                <div
                  onClick={() => toggleCollapse(group.layerId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 16px',
                    backgroundColor: isDark ? '#2d2f2f' : '#f4f6f8',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onMouseEnter={e =>
                  {
                    e.currentTarget.style.backgroundColor = isDark ? '#3a3c3c' : '#ebeef1';
                  }}
                  onMouseLeave={e =>
                  {
                    e.currentTarget.style.backgroundColor = isDark ? '#2d2f2f' : '#f4f6f8';
                  }}
                >
                  {/* Expand/collapse arrow */}
                  <span
                    style={{
                      fontSize: 10,
                      color: isDark ? '#6e6860' : '#6b7280',
                      width: 12,
                      flexShrink: 0,
                      transition: 'transform 0.15s',
                      display: 'inline-block',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    }}
                  >
                    ▼
                  </span>

                  {/* Layer color bar */}
                  <div
                    style={{
                      width: 4,
                      height: 16,
                      borderRadius: 2,
                      backgroundColor: group.layerColor ?? (isDark ? '#4a4c4c' : '#d1d5db'),
                      flexShrink: 0,
                    }}
                  />

                  {/* Layer-level checkbox (select/deselect all) */}
                  <LayerCheckbox
                    checked={allVisible}
                    indeterminate={someVisible}
                    onChange={e => toggleLayer(e, group.columns)}
                  />

                  {/* Layer name */}
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: isDark ? '#a9a093' : '#374151',
                      flex: 1,
                    }}
                  >
                    {group.layerName}
                  </span>

                  {/* Column count hint */}
                  <span style={{ fontSize: 11, color: isDark ? '#6e6860' : '#9ca3af' }}>
                    {visibleCount}/{keys.length}
                  </span>
                </div>

                {/* Column rows — hidden when collapsed */}
                {!isCollapsed &&
                  group.columns.map(col =>
                  {
                    const key = getColumnApiKey(col);
                    const isVisible = !localHidden.has(key);
                    return (
                      <div
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '5px 16px 5px 44px',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${isDark ? '#4a4c4c' : '#f9fafb'}`,
                          backgroundColor: isDark ? '#333535' : '#fff',
                        }}
                        onClick={() => toggleColumn(key)}
                        onMouseEnter={e =>
                        {
                          e.currentTarget.style.backgroundColor = isDark ? '#3a3c3c' : '#f9fafb';
                        }}
                        onMouseLeave={e =>
                        {
                          e.currentTarget.style.backgroundColor = isDark ? '#333535' : '#fff';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumn(key)}
                          onClick={e => e.stopPropagation()}
                          style={{
                            accentColor: '#5dade2',
                            cursor: 'pointer',
                            width: 14,
                            height: 14,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            color: isDark ? '#a9a093' : '#374151',
                            userSelect: 'none',
                          }}
                        >
                          {col.title}
                        </span>
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            padding: '10px 16px',
            borderTop: `1px solid ${isDark ? '#4a4c4c' : '#e5e7eb'}`,
            backgroundColor: isDark ? '#2d2f2f' : '#fafafa',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: '#929498',
              border: '1px solid #929498',
              borderRadius: 0,
              color: '#fff',
              fontSize: 12,
              lineHeight: 1.5,
              padding: '4px 12px',
              cursor: 'pointer',
            }}
            onMouseEnter={e =>
            {
              e.currentTarget.style.background = '#77797c';
              e.currentTarget.style.borderColor = '#77797c';
            }}
            onMouseLeave={e =>
            {
              e.currentTarget.style.background = '#929498';
              e.currentTarget.style.borderColor = '#929498';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            style={{
              background: '#62accb',
              border: '1px solid #62accb',
              borderRadius: 0,
              color: '#fff',
              fontSize: 12,
              lineHeight: 1.5,
              padding: '4px 12px',
              cursor: 'pointer',
            }}
            onMouseEnter={e =>
            {
              e.currentTarget.style.background = '#508ca6';
              e.currentTarget.style.borderColor = '#508ca6';
            }}
            onMouseLeave={e =>
            {
              e.currentTarget.style.background = '#62accb';
              e.currentTarget.style.borderColor = '#62accb';
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}
